
import React from 'react';
import { AnalysisResult } from '../types';
import { createRiskHeatmapData } from '../utils';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import CustomTooltip from '../CustomTooltip';

type RiskMitigationTabProps = {
  analysisResult: AnalysisResult;
};

const RiskMitigationTab = ({ analysisResult }: RiskMitigationTabProps) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-secondary/30 rounded-lg p-6 border border-primary/10 space-y-4">
        <h4 className="text-lg font-semibold text-primary mb-4">Risk Mitigation Strategies</h4>
        <div className="space-y-3">
          {analysisResult.riskMitigation?.map((risk, index) => (
            <div 
              key={index} 
              className="bg-secondary/50 rounded-lg p-4 border border-primary/10"
            >
              <div className="flex justify-between items-center mb-2">
                <h5 className="font-medium text-primary">{risk.risk}</h5>
                <span 
                  className={`px-2 py-1 rounded text-xs ${
                    risk.impact.toLowerCase().includes('high') 
                      ? 'bg-red-900/30 text-red-400' 
                      : risk.impact.toLowerCase().includes('medium')
                      ? 'bg-yellow-900/30 text-yellow-400'
                      : 'bg-green-900/30 text-green-400'
                  }`}
                >
                  {risk.impact} Impact
                </span>
              </div>
              <div className="bg-secondary/30 rounded p-3 mt-2">
                <h6 className="text-xs font-medium text-white/70 mb-1">Mitigation Strategy</h6>
                <p className="text-sm text-white">{risk.mitigation}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      <div className="bg-secondary/30 rounded-lg p-6 border border-primary/10">
        <h4 className="text-lg font-semibold text-primary mb-4">Risk Impact Heatmap</h4>
        <div className="h-96 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={createRiskHeatmapData(analysisResult)}
              layout="vertical"
              margin={{ top: 5, right: 10, left: 10, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#444" />
              <XAxis 
                type="number" 
                domain={[0, 100]} 
                stroke="#888" 
                tick={{ fill: '#fff', fontSize: 12 }}
              />
              <YAxis 
                dataKey="name" 
                type="category"
                width={150}
                stroke="#888" 
                tick={{ fill: '#fff', fontSize: 12 }}
              />
              <Tooltip
                content={<CustomTooltip />}
                formatter={(value) => [`Impact Level: ${value}%`, 'Risk Severity']}
                labelFormatter={(label) => [`Risk: ${label}`]}
              />
              <Bar 
                dataKey="value" 
                fill="#ff7300" 
                background={{ fill: '#3b3b5e' }}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-3 text-center">
          <p className="text-xs text-gray-400">
            Impact Scale: 0 = Negligible Impact, 25 = Low Impact, 50 = Moderate Impact, 75 = High Impact, 100 = Critical/Severe Impact
          </p>
        </div>
      </div>
    </div>
  );
};

export default RiskMitigationTab;
